"use client";

/** NOTED CONFLICTS (V33 revised, Tom 2026-10-26: "allow it, but be loud
 *  about the temproray inconsistency - and warn when doing it"). A change in
 *  a draft or proposal that left a route conflict is saved, and the
 *  conflict is noted: the write's answer warns at once, naming it; the
 *  review and the page's alert list it until it is settled, each with Align
 *  and Keep as override; a commit, accept or merge refused for it names each
 *  one, with the same two actions. An island the reader cannot see is never
 *  named. */

import React, { useState } from "react";
import { graphStore } from "@sourcepilot/framework/graph-store";
import { forgetNotedConflict, type NotedConflict } from "@sourcepilot/ui/framework/data/pins/noted-conflicts";
import { versioningClient } from "@sourcepilot/ui/framework/data/versioning-client";
import { toRefusal } from "@sourcepilot/ui/framework/data/refusal";

const short = (c: string) => c.slice(0, 10);
type Island = string | { hidden: true };
const hiddenIsland = (i: Island): i is { hidden: true } => typeof i === "object" && !!i && i.hidden === true;

function islandName(i: Island): string {
  if (hiddenIsland(i)) return "an island you cannot see";
  try {
    const n = graphStore.getNode(i) as { name?: unknown } | null;
    if (n && typeof n.name === "string" && n.name) return n.name;
  } catch {
    /* not held */
  }
  return "another island";
}

/** the conflict's island, in words: its name, else "an island you cannot see" */
export function notedIsland(c: NotedConflict): string {
  if (c.hidden || !c.island) return "an island you cannot see";
  return c.name || islandName(c.island);
}

/** "A → B → motor": a route, nearest first, ending at the island */
function routeWords(route: Island[], c: NotedConflict): string {
  if (route.length === 1) return "this project's own pin";
  return route.map((i, k) => (k === route.length - 1 ? notedIsland(c) : islandName(i))).join(" → ");
}

/** One conflict, named: the island, the version this project uses and each
 *  version the other routes reach, with those routes. */
function ConflictLines({ c }: { c: NotedConflict }) {
  return (
    <>
      <p data-testid="noted-conflict-reason">{c.reason}</p>
      {!c.hidden && (c.pinned || c.routes.length > 0) && (
        <ul className="space-y-0.5 pl-3 text-xs" data-testid="noted-conflict-versions">
          {c.pinned && <li data-testid="noted-conflict-pinned">This project uses {notedIsland(c)} at <code className="font-mono">{short(c.pinned)}</code></li>}
          {c.routes.map((r) => (
            <li key={r.commit} data-testid="noted-conflict-route">
              {r.name ? `${r.name} at ` : ""}<code className="font-mono">{short(r.commit)}</code>
              {r.via.length > 0 && <>: through {r.via.map((v) => routeWords(v, c)).join("; ")}</>}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Align for one noted conflict, drawn by the host (VC-D: Align is the
 *  aligned pin's update, previewed first — it moves a pin, so what it
 *  removes is seen before it is confirmed). `done`: it was staged;
 *  `cancel`: closed without. */
export type NotedAlign = (props: { conflict: NotedConflict; holderId: string; branch: string; done: () => void; cancel: () => void }) => React.ReactNode;

type Settled = "align" | "override";

/** Align / Keep as override for one noted conflict. The lane is the
 *  conflict's own, else the host's, else (`inDraft`: the page's alert) this
 *  holder's draft. Align is offered where the host can draw its preview;
 *  Keep where the conflict names this project's pin. */
function Settle({ c, holderId, branch, inDraft, alignWith, onSettled }: {
  c: NotedConflict; holderId?: string; branch?: string; inDraft?: boolean; alignWith?: NotedAlign; onSettled?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState<Settled | null>(null);
  const [aligning, setAligning] = useState<string | null>(null);
  const [resolved, setResolved] = useState<NotedConflict | null>(null);
  const holder = c.holder ?? holderId;
  if (done) return <p className="text-xs font-medium" data-testid="noted-conflict-settled" data-settled={done}>{done === "align" ? "Aligned in your draft." : "Kept as a deliberate override."}</p>;
  if (!holder) return <p className="text-xs" data-testid="noted-conflict-settle-elsewhere">Settle it on the project's references page.</p>;
  const align = c.hidden ? null : c.align ?? null;
  const canAlign = !!align && !!alignWith;
  // a hidden conflict is never kept from here (VC-D): review the references
  const canKeep = !c.hidden && !!c.island;
  const lane = async () => {
    const at = c.branch ?? branch ?? (inDraft ? (await versioningClient.overlayStatus(holder)).overlay : null);
    if (!at) throw { status: 409, detail: { code: "invalid_target", reason: "Open the draft or proposal this conflict is in to settle it." } };
    return at;
  };
  const run = async (what: Settled) => {
    setBusy(true); setFailure(null);
    try {
      const at = await lane();
      if (what === "align") {
        // the pin Align moves: the conflict's, else this project's pin of that island
        if (!align!.pin_id) setResolved({ ...c, align: { ...align!, pin_id: align!.island === c.island && c.pin_id ? c.pin_id : (await versioningClient.pinOfIsland(holder, at, align!.island)).pin_id } });
        setAligning(at);
        return;
      }
      await versioningClient.keepNotedConflict(holder, at, c as NotedConflict & { island: string });
      setDone("override");
      onSettled?.();
    } catch (e) {
      setFailure(toRefusal(e, what === "align" ? "The alignment could not be previewed." : "The conflict was not kept as an override.").reason);
    } finally {
      setBusy(false);
    }
  };
  const at = align ? c.routes.find((r) => r.commit === align.commit)?.name : null;
  return (
    <div className="mt-1 space-y-1 text-xs">
      {aligning && alignWith ? (
        <div data-testid="noted-conflict-aligning">
          {alignWith({ conflict: resolved ?? c, holderId: holder, branch: aligning,
            done: () => { setAligning(null); setDone("align"); forgetNotedConflict(holder, c); onSettled?.(); },
            cancel: () => setAligning(null) })}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {align && canAlign && (
            <button type="button" disabled={busy} onClick={() => void run("align")} data-testid="noted-conflict-align"
              className="rounded border border-amber-400 bg-white px-2 py-0.5 disabled:opacity-50">
              Align: use {at ? `${at} at ` : ""}<code className="font-mono">{short(align.commit)}</code>
            </button>
          )}
          {canKeep && (
            <button type="button" disabled={busy} onClick={() => void run("override")} data-testid="noted-conflict-override"
              className="rounded border border-amber-400 bg-white px-2 py-0.5 disabled:opacity-50">
              Keep as override
            </button>
          )}
        </div>
      )}
      {c.hidden
        ? <p data-testid="noted-conflict-hidden">Someone with access to it must review the references and settle this one.</p>
        : !align
          ? <p>The other references disagree among themselves, so there is no one version to align to.</p>
          : null}
      {!c.hidden && align && !canAlign && (
        <p data-testid="noted-conflict-settle-elsewhere">Align it on this project's references page.</p>
      )}
      {failure && <p role="alert" className="text-red-800" data-testid="noted-conflict-settle-failed">{failure}</p>}
    </div>
  );
}

/** Every noted conflict, loudly, each with Align and Keep as override. */
export function NotedConflictList({ conflicts, holderId, branch, inDraft, wouldLeave, alignWith, blocks = "Commit, accept and merge", onSettled, testId = "noted-conflicts" }: {
  conflicts: NotedConflict[];
  holderId?: string;
  branch?: string;
  /** no lane given or named: settle in this holder's draft (the page's alert) */
  inDraft?: boolean;
  /** not noted yet: what a change (an undo preview, or one refused for it)
   *  would leave — named, with nothing to settle */
  wouldLeave?: string;
  /** draws Align's preview (absent: aligned on the references page) */
  alignWith?: NotedAlign;
  /** what stays refused until each is settled */
  blocks?: string;
  onSettled?: (c: NotedConflict) => void;
  testId?: string;
}) {
  if (!conflicts.length) return null;
  return (
    <div role="alert" data-testid={testId} data-count={conflicts.length}
      className="space-y-2 rounded border-2 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-950">
      {wouldLeave ? <p className="font-medium">
        {wouldLeave} {conflicts.length === 1 ? "a reference conflict" : `${conflicts.length} reference conflicts`}: a temporary inconsistency.
      </p> : <p className="font-medium">
        {conflicts.length === 1 ? "A reference conflict is noted" : `${conflicts.length} reference conflicts are noted`}: a temporary inconsistency.
        {" "}{blocks} stay blocked until {conflicts.length === 1 ? "it is" : "each is"} aligned or kept as a deliberate override.
      </p>}
      <ul className="space-y-2">
        {conflicts.map((c) => (
          <li key={`${c.branch ?? ""}:${c.key}`} data-testid="noted-conflict" data-key={c.key}>
            <ConflictLines c={c} />
            {!wouldLeave && <Settle c={c} holderId={holderId} branch={branch} inDraft={inDraft} alignWith={alignWith} onSettled={() => onSettled?.(c)} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The write's own warning, as soon as its answer arrives: it went through,
 *  and left these conflicts noted. `warning`: the server's one sentence,
 *  shown first, as it was sent. */
export function NotedConflictWarning({ conflicts, warning, onDismiss }: { conflicts: NotedConflict[]; warning?: string | null; onDismiss?: () => void }) {
  if (!conflicts.length && !warning) return null;
  return (
    <div role="alert" data-testid="noted-conflict-warning"
      className="space-y-1 rounded border-2 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-950">
      {warning && <p className="font-medium" data-testid="noted-conflict-warning-sentence">{warning}</p>}
      {conflicts.length > 0 && <>
        {!warning && <p className="font-medium">
          Saved, but this leaves {conflicts.length === 1 ? "a reference conflict" : `${conflicts.length} reference conflicts`}: a temporary inconsistency.
        </p>}
        <ul className="space-y-1">
          {conflicts.map((c) => <li key={`${c.branch ?? ""}:${c.key}`} data-testid="noted-conflict-warning-item"><ConflictLines c={c} /></li>)}
        </ul>
        {!warning && <p className="text-xs">Align each one or keep it as a deliberate override before committing: until then commit, accept and merge are refused, and reports and calculations that depend on it are incomplete.</p>}
      </>}
      {onDismiss && <button type="button" onClick={onDismiss} className="text-xs underline" data-testid="noted-conflict-warning-dismiss">Dismiss</button>}
    </div>
  );
}

/** "incomplete: conflicting pins" — a report or calculation that depends on
 *  a noted conflict, never a plain failure: the server's sentences, each
 *  naming the conflict (an island the reader cannot see: "an item you
 *  can't see"). */
export function ConflictingPinsNote({ sentences, testId = "conflicting-pins" }: { sentences: string[]; testId?: string }) {
  return (
    <span className="block text-xs text-amber-800" data-testid={testId}>
      {sentences.map((s, i) => <span key={i} className="block" data-testid={`${testId}-item`}>{s}</span>)}
    </span>
  );
}
